// frontend/src/utils/reference/entityScorer.js
// Owns: entity scoring

import {
  cloneEntityCandidate,
  sameEntity,
  sortEntitiesByScore,
} from './entityStateHelpers';

const ENTITY_SCORE_WEIGHTS = {
  EXPLICIT_NAME: 12,
  CURRENT_ENTITY_MATCH: 4,
  PREVIOUS_ACTIVE_MATCH: 2,
  PRONOUN_SUPPORT: 9,
  CARRY_FORWARD: 1,
  RELATED_CONTEXT: 2,
  HISTORICAL: 1,
  EXPLICIT_CORRECTION: 14,
  DEMOTED_PREVIOUS: -8,
  NON_WINNING_NAMED: 2,

  // freshness / anti-drift controls
  FRESH_TURN_EVIDENCE: 4,
  STALE_CARRY_FORWARD_PENALTY: -5,
};

function toBoolean(value) {
  return value === true;
}

function hasFreshTurnEvidence(candidate = null) {
  if (!candidate) return false;

  return (
    toBoolean(candidate?.signals?.explicitlyNamed) ||
    toBoolean(candidate?.signals?.pronounSupported) ||
    toBoolean(candidate?.signals?.corrected)
  );
}

function isStaleCarryForwardCandidate(candidate = null) {
  if (!candidate) return false;

  return (
    toBoolean(candidate?.signals?.carryForward) &&
    !hasFreshTurnEvidence(candidate)
  );
}

function getCandidateBaseScore({
  candidate = null,
  currentEntity = null,
  previousActiveEntity = null,
} = {}) {
  if (!candidate) return 0;

  let score = 0;

  if (toBoolean(candidate?.signals?.explicitlyNamed)) {
    score += ENTITY_SCORE_WEIGHTS.EXPLICIT_NAME;
  }

  if (toBoolean(candidate?.signals?.pronounSupported)) {
    score += ENTITY_SCORE_WEIGHTS.PRONOUN_SUPPORT;
  }

  if (toBoolean(candidate?.signals?.carryForward)) {
    score += ENTITY_SCORE_WEIGHTS.CARRY_FORWARD;
  }

  if (toBoolean(candidate?.signals?.related)) {
    score += ENTITY_SCORE_WEIGHTS.RELATED_CONTEXT;
  }

  if (toBoolean(candidate?.signals?.historical)) {
    score += ENTITY_SCORE_WEIGHTS.HISTORICAL;
  }

  if (toBoolean(candidate?.signals?.corrected)) {
    score += ENTITY_SCORE_WEIGHTS.EXPLICIT_CORRECTION;
  }

  if (sameEntity(candidate, currentEntity)) {
    score += ENTITY_SCORE_WEIGHTS.CURRENT_ENTITY_MATCH;
  }

  if (sameEntity(candidate, previousActiveEntity)) {
    score += ENTITY_SCORE_WEIGHTS.PREVIOUS_ACTIVE_MATCH;
  }

  if (hasFreshTurnEvidence(candidate)) {
    score += ENTITY_SCORE_WEIGHTS.FRESH_TURN_EVIDENCE;
  }

  if (isStaleCarryForwardCandidate(candidate)) {
    score += ENTITY_SCORE_WEIGHTS.STALE_CARRY_FORWARD_PENALTY;
  }

  return score;
}

function scoreEntityCandidate({
  candidate = null,
  currentEntity = null,
  previousActiveEntity = null,
} = {}) {
  if (!candidate) return null;

  const next = cloneEntityCandidate(candidate);
  next.score = getCandidateBaseScore({
    candidate: next,
    currentEntity,
    previousActiveEntity,
  });

  return next;
}

function scoreEntityCandidates({
  candidates = [],
  currentEntity = null,
  previousActiveEntity = null,
} = {}) {
  if (!Array.isArray(candidates) || candidates.length === 0) {
    return [];
  }

  const scored = candidates
    .map((candidate) =>
      scoreEntityCandidate({
        candidate,
        currentEntity,
        previousActiveEntity,
      })
    )
    .filter(Boolean);

  return sortEntitiesByScore(scored);
}

function boostWinningEntity(winner = null) {
  if (!winner) return null;

  const next = cloneEntityCandidate(winner);
  next.score = (next.score || 0) + ENTITY_SCORE_WEIGHTS.NON_WINNING_NAMED;
  return next;
}

function demotePreviousEntity(entity = null) {
  if (!entity) return null;

  const next = cloneEntityCandidate(entity);
  next.score = (next.score || 0) + ENTITY_SCORE_WEIGHTS.DEMOTED_PREVIOUS;
  return next;
}

function getTopScoringEntity(candidates = []) {
  if (!Array.isArray(candidates) || candidates.length === 0) {
    return null;
  }

  const sorted = sortEntitiesByScore(candidates);
  return sorted[0] || null;
}

function getScoredEntityMap(candidates = []) {
  if (!Array.isArray(candidates) || candidates.length === 0) {
    return {};
  }

  return candidates.reduce((acc, candidate) => {
    if (!candidate?.key) return acc;
    acc[candidate.key] = cloneEntityCandidate(candidate);
    return acc;
  }, {});
}

export {
  ENTITY_SCORE_WEIGHTS,
  getCandidateBaseScore,
  scoreEntityCandidate,
  scoreEntityCandidates,
  boostWinningEntity,
  demotePreviousEntity,
  getTopScoringEntity,
  getScoredEntityMap,
};